import { useCompanyReadDataContext } from '@aibees/elephant/context';
import {
  useCompanyVisibilityState,
  useSearchTableFromRoute,
} from '@aibees/elephant/hooks';
import { usePageType } from '@aibees/elephant/hooks/use-page-type';
import { SearchTable } from '@aibees/elephant/types';
import { IconProps, usePopover } from '@aibees/shared/components';
import { classnames } from '@aibees/shared/utils';
import { ButtonGroup } from '@mui/material';
import { memo, useMemo } from 'react';
import { CurrentCompanyContactDataItem } from '../current-company-contact-data-item';
import { CurrentCompanyContactDataPopover } from '../current-company-contact-data-popover';
import * as Styled from './styles';
import * as SX from './sx';
import type { CurrentCompanyContactDataProps as Props } from './types';

export const CurrentCompanyContactData = memo(
  ({
    className,
    iconOrder = 'vertical',
    hideDomains,
    size = 'small',
  }: Props) => {
    const { api } = useCompanyReadDataContext();
    const table = useSearchTableFromRoute();
    const { isCompanyPage } = usePageType();
    const isCompanyContext =
      table === SearchTable.Company || isCompanyPage || !!api?.data;
    const { showNoDataAvailable } = useCompanyVisibilityState();
    const shouldDisable = !isCompanyContext && showNoDataAvailable;

    const [domains, phones, emails] = useMemo(() => {
      return [
        api?.data?.link?.urls,
        api?.data?.contact?.phone?.raw ? [api?.data?.contact?.phone?.raw] : [],
        api?.data?.contact?.email ? [api?.data?.contact?.email] : [],
      ];
    }, [api?.data]);

    const emailPopoverState = usePopover();
    const phonePopoverState = usePopover();
    const domainsPopoverState = usePopover();

    const handleOpenEmailPopover: IconProps['onClick'] = (e) => {
      emailPopoverState.onOpen(
        api?.data?.contact?.email ? e.currentTarget : null
      );
    };

    const handleOpenDomainPopover: IconProps['onClick'] = (e) => {
      domainsPopoverState.onOpen(domains?.length ? e.currentTarget : null);
    };

    const handleOpenPhonePopover: IconProps['onClick'] = (e) => {
      phonePopoverState.onOpen(
        api?.data?.contact?.phone?.raw ? e.currentTarget : null
      );
    };

    return (
      <Styled.Wrapper
        className={classnames('CurrentCompanyContactData', className)}
        sx={SX.root}
      >
        <ButtonGroup
          size={size}
          variant="outlined"
          color="inherit"
          disabled={shouldDisable}
        >
          {!hideDomains && (
            <CurrentCompanyContactDataItem
              title="No Domains"
              icon="network"
              value={domains}
              iconOrder={iconOrder}
              onClick={handleOpenDomainPopover}
              isOpen={!!domainsPopoverState.open}
              size={size}
            />
          )}
          <CurrentCompanyContactDataItem
            title="No Role-Base Email"
            icon="email"
            value={emails}
            iconOrder={iconOrder}
            onClick={handleOpenEmailPopover}
            isOpen={!!emailPopoverState.open}
            size={size}
          />
          <CurrentCompanyContactDataItem
            title="No Corporate Dials"
            icon="phone"
            value={phones}
            iconOrder={iconOrder}
            onClick={handleOpenPhonePopover}
            isOpen={!!phonePopoverState.open}
            size={size}
          />
        </ButtonGroup>

        <CurrentCompanyContactDataPopover
          icon="network"
          type="network"
          state={domainsPopoverState}
          title="Company URLs"
          value={domains ?? []}
          copyValue={domains?.at(0) || ''}
        />
        <CurrentCompanyContactDataPopover
          icon="email"
          type="email"
          state={emailPopoverState}
          title="Role-Base Email"
          value={emails}
          copyValue={emails.at(0) || ''}
        />

        <CurrentCompanyContactDataPopover
          icon="phone"
          type="phone"
          state={phonePopoverState}
          title="Corporate Dials"
          value={phones}
          copyValue={
            api?.data?.contact?.phone?.sanitized
              ? api?.data?.contact?.phone?.sanitized
              : api?.data?.contact?.phone?.raw ?? ''
          }
        />
      </Styled.Wrapper>
    );
  }
);

CurrentCompanyContactData.displayName = 'CurrentCompanyContactData';
